import React, { useState } from 'react';
import Layout from '@components/layout/Layout';
import styled from 'styled-components';
import { styles } from '@components/utils';
import { Button } from '@repo/ui-components/shadcn/Button';
import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';
import KurgoContainer from '@components/utils/ContainerStyles';
import Breadcrumb from '@components/breadcrumb';
import { FormRow, StyledForm } from '@components/forms/StyledForm';
import createOpenGraphTags from '@repo/utils/createOpenGraphTags';

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
`;

const CalorieInatkeHeading = styled.h2`
  text-decoration: underline;
`;

const DogCalorieCalculator = () => {
  const [calories, setCalories] = useState(0);
  const [unit, setUnit] = useState(2.204);
  const [mass, setMass] = useState(0);
  const [activity, setActivity] = useState(1.0);

  const handleUnitChange = () => {
    const el = document.getElementById('unit') as HTMLSelectElement | null;
    if (el) setUnit(parseFloat(el.value));
  };

  const handleWeightChange = () => {
    const el = document.getElementById('mass') as HTMLInputElement | null;
    if (el) setMass(parseFloat(el.value));
  };

  const handleActivityChange = () => {
    const el = document.getElementById('activity') as HTMLSelectElement | null;
    if (el) setActivity(parseFloat(el.value));
  };

  const calculateCalories = e => {
    const base = 80;
    setCalories(Math.round(activity * base * (mass / unit) ** 0.75));
    e.preventDefault();
  };

  return (
    <Layout
      headTagData={{
        title: 'Dog Calorie Calculator: How Many Calories Should My Dog Eat',
        description:
          'Kurgo Dog Calorie  Calculator helps you figure out how much your dog should eat to maintain peak health',
        keywords:
          'dog calorie calculator, dog calorie counter, how much should my dog eat',
        meta: createOpenGraphTags(
          {
            title:
              'Dog Calorie Calculator: How Many Calories Should My Dog Eat',
            description:
              'Kurgo Dog Calorie  Calculator helps you figure out how much your dog should eat to maintain peak health',
            url: '/dog-calorie-calculator',
          },
          'kurgo'
        ),
      }}
    >
      <KurgoContainer backgroundColor={styles.colors.gray[100]}>
        <Breadcrumb
          elements={[
            { text: 'Home', link: '/' },
            { text: 'Our Story', link: '/dog-calorie-calculator' },
          ]}
        />
        <PageWrapper>
          <TextContainer>
            <h1>Dog Calorie Calculator</h1>
          </TextContainer>
          <strong>How many calories in dog food?</strong>
          <p>
            Just when everyone sits down for dinner, you hear the pounding of
            four furry paws as your Chocolate Lab dashes towards the dining
            room. She skids to a stop next to your chair, and drool begins to
            pool on the floor under her open mouth as she stares longingly at
            the delicious meal atop the table. While she patiently waits for a
            morsel of food to drop you think to yourself: is she still hungry?
            Am I not feeding her enough? How much food should I be feeding her?
          </p>
          <p>
            Although dogs communicate in other ways, they cannot tell us what
            they’re thinking. It can be difficult to understand what kind of
            food they need or how much they really should be eating.
          </p>
          <p>
            In order to better understand dogs’ daily food requirements, the
            University of California-Davis School of Veterinary Medicine created
            the MER, or the “maintenance energy requirement.” Your pup’s MER
            represents the amount of calories she needs to consume in order to
            do her everyday activities. Weight and exercise habits are
            contributing factors in your dog’s MER.
          </p>
          <p>
            We created a handy calculator to eliminate all the guesswork in
            determining your dog’s MER. Just plug in your pup’s information and
            voila! It will tell you how many calories your dog should consume
            every day. We want all pet parents to #GoTogether confidently with
            their pups, and this calorie counter is one way for dog owners to
            educate themselves on their dog’s proper nutrition.
          </p>
          <StyledForm onSubmit={calculateCalories}>
            <h2>Dog Caloric Requirement Calculator</h2>
            <FormRow>
              <label htmlFor="mass">
                Weight{' '}
                <input
                  name="mass"
                  id="mass"
                  type="number"
                  onChange={handleWeightChange}
                />
              </label>
            </FormRow>
            <FormRow>
              <label htmlFor="unit">
                Unit{' '}
                <select id="unit" name="unit" onChange={handleUnitChange}>
                  <option value="2.204">Pounds</option>
                  <option value="1.000">Kilograms</option>
                </select>
              </label>
            </FormRow>
            <FormRow>
              <label htmlFor="activity">
                Activity Level{' '}
                <select
                  id="activity"
                  name="activity"
                  onChange={handleActivityChange}
                >
                  <option value="1.0">Normal</option>
                  <option value="0.8">Less Active</option>
                  <option value="1.2">Very Active</option>
                </select>
              </label>
            </FormRow>
            <br />
            <Button type="submit" onClick={calculateCalories}>
              Calculate
            </Button>
          </StyledForm>

          <p>Calories: {calories > 0 && calories}</p>

          <p>
            Now that you know your dog’s MER, you can determine how much food
            she needs by checking out our food brand chart. The chart shows the
            average calories per cup from many popular food brands,* so you can
            see how many cups of food your dog needs every day to stay healthy
            and fit. If you’re planning a hiking trip, you can also get an idea
            of how much extra food you’ll need to bring so that your pup can
            safely replenish the calories she burns.
          </p>
          <p>
            Just remember that this calorie counter is not meant to take the
            place of your veterinarian&apos;s recommendations. It should give
            you a general idea of what your adult dog should be eating. Every
            breed is different, every dog is different, and the same dog will
            need more or less as they age.
          </p>
          <p>
            <strong>*Notice:</strong> The calorie counts below are for general
            guidance only, please consult your vet for a specific caloric
            recommendation. The guidelines below assume caloric needs for an
            average lightly active adult spayed or neutered dog; are between one
            and seven years old, plus getting less than 30 minutes aerobic
            activity per day.
          </p>
          <CalorieInatkeHeading>
            Suggested Calorie Intake for Dogs by Weight
          </CalorieInatkeHeading>
          <p>
            <strong>10 lbs.</strong> 200 to 275 calories
          </p>
          <p>
            <strong>20 lbs.</strong> 325 to 400 calories
          </p>
          <p>
            <strong>50 lbs.</strong> 700 to 900 calories
          </p>
          <p>
            <strong>70 lbs.</strong> 900 to 1050 calories
          </p>
          <p>
            <strong>90 lbs.</strong> 1100 to 1350 calories
          </p>
          <p>&nbsp;</p>
        </PageWrapper>
      </KurgoContainer>
    </Layout>
  );
};

export default DogCalorieCalculator;
